import { RefObject, useLayoutEffect, useRef, useState } from 'react';
import { observeElementResize } from './observeElementResize';

type Values = [number, number, number, number, number];
const expanded: Values = [0, 0, 1, 1, 1];

/** Independent critically damped springs. Retargeting preserves presentation and velocity. */
export function useDockDialogMotion(open: boolean, targetRef: RefObject<HTMLButtonElement | null> | undefined,
  dialogRef: RefObject<HTMLDivElement | null>, backdropRef: RefObject<HTMLDivElement | null>) {
  const [present, setPresent] = useState(open);
  const motion = useRef<{ values: Values; velocity: Values } | undefined>(undefined);
  const visible = open || present;
  useLayoutEffect(() => {
    if (!visible) return;
    const dialog = dialogRef.current!;
    const backdrop = backdropRef.current!;
    const scrim = backdrop.querySelector<HTMLElement>('.game-dialog-scrim')!;
    if (!targetRef) { setPresent(open); return; }
    setPresent(true);
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let reduced = !!preference?.matches;
    let target: Values = [...expanded];
    let frame = 0;
    let last = performance.now();
    const measure = () => {
      // Read the layout rectangle without feeding our current transform back into the target.
      const transform = dialog.style.transform;
      dialog.style.transform = 'none';
      const surface = dialog.getBoundingClientRect();
      dialog.style.transform = transform;
      const button = targetRef.current?.isConnected ? targetRef.current.getBoundingClientRect() : undefined;
      const anchored = !reduced && button && button.width > 0 && button.height > 0 && surface.width > 0 && surface.height > 0;
      const collapsed: Values = anchored ? [
        button.left + button.width / 2 - surface.left - surface.width / 2,
        button.top + button.height / 2 - surface.top - surface.height / 2,
        Math.min(1, button.width / surface.width), Math.min(1, button.height / surface.height), 0,
      ] : reduced || !motion.current ? [0, 0, 1, 1, 0] : [
        motion.current.values[0], motion.current.values[1], motion.current.values[2], motion.current.values[3], 0,
      ];
      target = open ? [...expanded] : collapsed;
      if (!motion.current) motion.current = { values: open ? collapsed : [...expanded], velocity: [0, 0, 0, 0, 0] };
      // Preference changes remove spatial movement immediately, retaining the fade.
      if (reduced) {
        motion.current.values.splice(0, 4, 0, 0, 1, 1);
        motion.current.velocity.splice(0, 4, 0, 0, 0, 0);
      }
    };
    const paint = () => {
      const [x, y, sx, sy, opacity] = motion.current!.values;
      dialog.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${sx}, ${sy})`;
      dialog.style.opacity = String(Math.max(0, Math.min(1, opacity)));
      scrim.style.opacity = String(Math.max(0, Math.min(1, opacity)));
    };
    const tick = (now: number) => {
      const dt = Math.min(Math.max((now - last) / 1000, 0), 0.064);
      last = now;
      const state = motion.current!;
      const omega = reduced ? 48 : 26;
      const decay = Math.exp(-omega * dt);
      let settled = true;
      state.values.forEach((value, index) => {
        const displacement = value - target[index];
        const coefficient = state.velocity[index] + omega * displacement;
        state.values[index] = target[index] + (displacement + coefficient * dt) * decay;
        state.velocity[index] = (state.velocity[index] - omega * coefficient * dt) * decay;
        const tolerance = index < 2 ? 0.1 : 0.001;
        if (Math.abs(state.values[index] - target[index]) > tolerance || Math.abs(state.velocity[index]) > tolerance * 10) settled = false;
      });
      paint();
      if (!settled) frame = requestAnimationFrame(tick);
      else {
        state.values = [...target];
        state.velocity = [0, 0, 0, 0, 0];
        paint();
        dialog.style.willChange = '';
        backdrop.dataset.motion = 'settled';
        if (!open) { motion.current = undefined; setPresent(false); }
      }
    };
    const retarget = () => {
      measure();
      cancelAnimationFrame(frame);
      last = performance.now();
      frame = requestAnimationFrame(tick);
    };
    const changePreference = () => { reduced = !!preference?.matches; retarget(); };
    measure();
    paint();
    dialog.style.willChange = 'transform, opacity';
    backdrop.dataset.motion = open ? 'opening' : 'closing';
    frame = requestAnimationFrame(tick);
    const disconnect = observeElementResize([dialog, targetRef.current], retarget);
    window.addEventListener('resize', retarget);
    preference?.addEventListener?.('change', changePreference);
    return () => {
      cancelAnimationFrame(frame);
      disconnect();
      window.removeEventListener('resize', retarget);
      preference?.removeEventListener?.('change', changePreference);
    };
  }, [open, visible, targetRef, dialogRef, backdropRef]);
  return visible;
}
